import React, { Component } from 'react';
import { fetchApi } from '../../Network/NetworkService';
import '../../Assets/Pages/adminSuiviLists.css';

// Configuration des icônes zmdi selon l'évolution de la santé
const evolutionConfig = {
    "Forte amélioration": { icon: 'zmdi-check-circle', cls: "green" },
    "Légère amélioration": { icon: 'zmdi-mood', cls: "orange" },
    "Aucun changement": { icon: 'zmdi-mood-bad', cls: "yellow" },
    "Cela s'aggrave": { icon: 'zmdi-mood-bad', cls: "red" },
};

const MONTHS_FR = [
    "janv.", "févr.", "mars", "avr.", "mai", "juin",
    "juil.", "août", "sept.", "oct.", "nov.", "déc.",
];

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }

function patientName(s) {
    if (!s.userId) return "Patient inconnu";
    return `${s.userId.FName ?? ""} ${s.userId.LName ?? ""}`.trim() || "Patient inconnu";
}

function initials(f, l) {
    return `${(f?.[0] ?? "?").toUpperCase()}${(l?.[0] ?? "").toUpperCase()}`;
}

// YN Component
class YN extends Component {
    render() {
        const { value } = this.props;
        if (!value) return <span className="admin-sd-muted">—</span>;
        return <span className={`admin-sd-yn ${value === "Oui" ? "yes" : "no"}`}>{value}</span>;
    }
}

// Stat Component
class Stat extends Component {
    render() {
        const { label, value, color } = this.props;
        return (
            <div className="admin-sd-stat" style={{ "--accent": color }}>
                <p className="admin-sd-stat-label">{label}</p>
                <p className="admin-sd-stat-value">{value}</p>
            </div>
        );
    }
}

// Provenance du suivi : utm_source enregistré à la soumission du formulaire.
// "whatsapp" = lien généré depuis l'admin ; null = parcours normal (naturel).
class SourcePill extends Component {
    render() {
        const { source } = this.props;
        if (source === "whatsapp") {
            return (
                <span className="admin-sd-pill whatsapp">
                    <i className="zmdi zmdi-whatsapp" />
                    WhatsApp
                </span>
            );
        }
        if (source) {
            return <span className="admin-sd-pill gray">{source}</span>;
        }
        return <span className="admin-sd-pill gray">Naturel</span>;
    }
}

export class SuiviDermatoTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            data: [],
            loading: true,
            error: null,
            mounted: false,
            search: "",
            searchBy: "patientName",
            sort: "desc",
            filterEvolution: "all",
            filterSource: "all",
            page: 1,
            dialog: { open: false, suivi: null, index: 0 },
        };

        this.pageSize = 8;
    }

    componentDidMount() {
        this.setState({ mounted: true });
        this._fetchSuivis();
    }

    _fetchSuivis() {
        this.setState({ loading: true, error: null });

        fetchApi('/suivi/dermato', { method: 'GET' })
            .then(res => {
                this.setState({ data: res.data || [], loading: false });
            })
            .catch(err => {
                console.error('Error fetching suivi-dermato:', err);
                this.setState({ error: err, loading: false });
            });
    }

    _getFiltered() {
        const { data, filterEvolution, filterSource, search, searchBy, sort } = this.state;

        let rows = [...data];

        if (filterEvolution !== "all") {
            rows = rows.filter(r => r.q1_skinEvolution === filterEvolution);
        }
        // Provenance : suivis remplis depuis le lien envoyé au patient
        // (utm_source=whatsapp posé par le générateur de lien de l'admin).
        if (filterSource !== "all") {
            rows = rows.filter(r =>
                filterSource === "whatsapp" ? r.source === "whatsapp" : r.source !== "whatsapp"
            );
        }
        if (search.trim()) {
            const q = search.toLowerCase();
            rows = rows.filter(r =>
                searchBy === "patientName"
                    ? patientName(r).toLowerCase().includes(q)
                    : (r.q1_skinEvolution || "").toLowerCase().includes(q)
            );
        }
        rows.sort((a, b) => {
            const da = new Date(a.createdAt).getTime();
            const db = new Date(b.createdAt).getTime();
            return sort === "desc" ? db - da : da - db;
        });
        return rows;
    }

    _getStats() {
        const { data } = this.state;
        return {
            total: data.length,
            attention: data.filter(d => d.requiresAttention).length,
            withPhotos: data.filter(d => d.photoFiles && d.photoFiles.length > 0).length,
            improving: data.filter(d =>
                ["Forte amélioration", "Légère amélioration"].includes(d.q1_skinEvolution)
            ).length,
            viaLink: data.filter(d => d.source === "whatsapp").length,
            // Pendants des compteurs « Abonnement » / « Nouvelle consult. » de
            // la page pathologie : l'écran de fin du suivi dermato propose
            // « Activer mon accompagnement » (abonnement) et le formulaire
            // demande l'intention de reconsulter (q4, posée quand l'état
            // stagne ou s'aggrave). Les deux compteurs « réels » sont écrits
            // par feeli-server : resultingSubscriptionAt à l'activation
            // Stripe, resultingRequestId à la reconsultation (même donnée que
            // la page /suivis/reconsultation).
            subscriptions: data.filter(d => d.resultingSubscriptionAt).length,
            reconsultIntent: data.filter(d => d.q4_reconsultDermatologist === "Oui").length,
            reconsulted: data.filter(d => d.resultingRequestId).length,
        };
    }

    _openPhotos(s) {
        if (!s.photoFiles || s.photoFiles.length === 0) return;
        this.setState({ dialog: { open: true, suivi: s, index: 0 } });
    }

    _closeDialog() {
        this.setState(prev => ({ dialog: { ...prev.dialog, open: false } }));
    }

    render() {
        const { mounted, search, searchBy, sort, filterEvolution, filterSource, page, dialog, loading, error } = this.state;
        const filtered = this._getFiltered();
        const stats = this._getStats();

        const totalPages = Math.max(1, Math.ceil(filtered.length / this.pageSize));
        const currentPage = Math.min(page, totalPages);
        const paginated = filtered.slice(
            (currentPage - 1) * this.pageSize,
            currentPage * this.pageSize,
        );

        const currentPhoto = dialog.suivi?.photoFiles[dialog.index];

        return (
            <div className="admin-sd-root">
                <div className="admin-sd-container">
                    {/* Header */}
                    <div className="admin-sd-header">
                        <div>
                            <h1>Suivis dermatologiques</h1>
                            <p>Consultez l'évolution cutanée de vos patients et leurs photos de suivi.</p>
                        </div>
                        <span className="admin-sd-header-badge">{this.state.data.length} suivis</span>
                    </div>

                    {/* Stats */}
                    <div className="admin-sd-stats">
                        <Stat label="Total" value={stats.total} color="#4e95fb" />
                        <Stat label="En amélioration" value={stats.improving} color="#02ce26" />
                        <Stat label="Avec photos" value={stats.withPhotos} color="#2b7cd1" />
                        <Stat label="Abonnements" value={stats.subscriptions} color="#02ce26" />
                        <Stat label="Souhaite reconsulter" value={stats.reconsultIntent} color="#f59e0b" />
                        <Stat label="Reconsultations" value={stats.reconsulted} color="#2b7cd1" />
                        <Stat label="Attention requise" value={stats.attention} color="#ff4858" />
                        <Stat label="Via lien WhatsApp" value={stats.viaLink} color="#25D366" />
                    </div>

                    {/* Toolbar */}
                    <div className="admin-sd-toolbar">
                        <div className="admin-sd-search">
                            <i className="zmdi zmdi-search" />
                            <input
                                className="admin-sd-input"
                                placeholder={
                                    searchBy === "patientName"
                                        ? "Rechercher un patient..."
                                        : "Rechercher une évolution..."
                                }
                                value={search}
                                onChange={e => this.setState({ search: e.target.value, page: 1 })}
                            />
                        </div>
                        <select
                            className="admin-sd-select"
                            value={searchBy}
                            onChange={e => this.setState({ searchBy: e.target.value })}
                        >
                            <option value="patientName">Par patient</option>
                            <option value="evolution">Par évolution</option>
                        </select>
                        <select
                            className="admin-sd-select"
                            value={filterEvolution}
                            onChange={e => this.setState({ filterEvolution: e.target.value, page: 1 })}
                        >
                            <option value="all">Toutes les évolutions</option>
                            <option value="Forte amélioration">Forte amélioration</option>
                            <option value="Légère amélioration">Légère amélioration</option>
                            <option value="Aucun changement">Aucun changement</option>
                            <option value="Cela s'aggrave">Cela s'aggrave</option>
                        </select>
                        <select
                            className="admin-sd-select"
                            value={filterSource}
                            onChange={e => this.setState({ filterSource: e.target.value, page: 1 })}
                        >
                            <option value="all">Toutes provenances</option>
                            <option value="whatsapp">Via lien WhatsApp</option>
                            <option value="other">Autres</option>
                        </select>
                        <button
                            className="admin-sd-btn"
                            onClick={() =>
                                this.setState(prev => ({ sort: prev.sort === "desc" ? "asc" : "desc" }))
                            }
                        >
                            <i className={sort === "desc" ? "zmdi zmdi-sort-amount-desc" : "zmdi zmdi-sort-amount-asc"} />
                            {sort === "desc" ? "Plus récents" : "Plus anciens"}
                        </button>
                    </div>

                    {/* Table */}
                    <div className="admin-sd-card">
                        {loading ? (
                            <div className="admin-sd-loader-wrap">
                                <div className="admin-sd-spinner"></div>
                                <p>Chargement des suivis...</p>
                            </div>
                        ) : error ? (
                            <div className="admin-sd-loader-wrap">
                                <i className="zmdi zmdi-alert-triangle" style={{ color: '#ff4858', fontSize: 36 }} />
                                <p>Erreur lors du chargement</p>
                            </div>
                        ) : (
                            <>
                                <div className="admin-sd-table-wrap">
                                    <table className="admin-sd-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 80 }}>Date</th>
                                                <th>Patient</th>
                                                <th>Évolution</th>
                                                <th>Médecin</th>
                                                <th>Source</th>
                                                <th className="admin-sd-th-center">Soins</th>
                                                <th className="admin-sd-th-center">Reconsult.</th>
                                                <th>Photos</th>
                                                <th className="admin-sd-th-right">Statut</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginated.length === 0 && (
                                                <tr>
                                                    <td colSpan={9} className="admin-sd-empty">
                                                        Aucun suivi ne correspond à votre recherche.
                                                    </td>
                                                </tr>
                                            )}
                                            {paginated.map(s => {
                                                const d = new Date(s.createdAt);
                                                const cfg = evolutionConfig[s.q1_skinEvolution] || {
                                                    icon: 'zmdi-help',
                                                    cls: "gray",
                                                };
                                                return (
                                                    <tr key={s._id}>
                                                        <td>
                                                            <div className="admin-sd-date">
                                                                <span className="admin-sd-date-day">
                                                                    {mounted ? d.getDate() : d.getUTCDate()}
                                                                </span>
                                                                <span className="admin-sd-date-month">
                                                                    {MONTHS_FR[mounted ? d.getMonth() : d.getUTCMonth()]}
                                                                </span>
                                                                <span className="admin-sd-date-time">
                                                                    {mounted
                                                                        ? `${pad(d.getHours())}:${pad(d.getMinutes())}`
                                                                        : `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="admin-sd-patient">
                                                                <div className="admin-sd-avatar">
                                                                    {initials(s.userId?.FName, s.userId?.LName)}
                                                                </div>
                                                                <div className="admin-sd-patient-info">
                                                                    <div className="admin-sd-patient-name">{patientName(s)}</div>
                                                                    <div className="admin-sd-patient-email">
                                                                        {s.userId?.email ?? "—"}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`admin-sd-pill ${cfg.cls}`}>
                                                                <i className={`zmdi ${cfg.icon}`} />
                                                                {s.q1_skinEvolution}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {s.docteurName ? (
                                                                <span className="admin-sd-doctor" title="Médecin choisi dans le lien envoyé au patient">
                                                                    <i className="zmdi zmdi-account-box" />
                                                                    Dr {s.docteurName}
                                                                </span>
                                                            ) : s.doctorId ? (
                                                                <span className="admin-sd-doctor">
                                                                    <i className="zmdi zmdi-account-box" />
                                                                    Dr {s.doctorId.FName} {s.doctorId.LName}
                                                                </span>
                                                            ) : (
                                                                <span className="admin-sd-muted">—</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <SourcePill source={s.source} />
                                                        </td>
                                                        <td className="admin-sd-td-center">
                                                            <YN value={s.q3_continueCare} />
                                                        </td>
                                                        <td className="admin-sd-td-center">
                                                            <YN value={s.q4_reconsultDermatologist} />
                                                        </td>
                                                        <td>
                                                            {s.photoFiles && s.photoFiles.length > 0 ? (
                                                                <button
                                                                    className="admin-sd-photos-btn"
                                                                    onClick={() => this._openPhotos(s)}
                                                                >
                                                                    <span className="admin-sd-thumb-stack">
                                                                        {s.photoFiles.slice(0, 3).map(p => (
                                                                            <img key={p._id} src={p.path} alt={p.name} loading="lazy" />
                                                                        ))}
                                                                    </span>
                                                                    <span>{s.photoFiles.length}</span>
                                                                    <i className="zmdi zmdi-eye" style={{ fontSize: 11 }} />
                                                                </button>
                                                            ) : (
                                                                <span className="admin-sd-photos-empty">
                                                                    <i className="zmdi zmdi-camera" />
                                                                    Aucune
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="admin-sd-td-right">
                                                            {s.requiresAttention ? (
                                                                <span className="admin-sd-pill red">
                                                                    <i className="zmdi zmdi-alert-triangle" />
                                                                    Attention
                                                                </span>
                                                            ) : (
                                                                <span className="admin-sd-pill gray">
                                                                    <i className="zmdi zmdi-check" />
                                                                    OK
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                <div className="admin-sd-pagination">
                                    <span>
                                        Affichant{" "}
                                        <strong>
                                            {filtered.length === 0 ? 0 : (currentPage - 1) * this.pageSize + 1}
                                        </strong>
                                        {" – "}
                                        <strong>{Math.min(currentPage * this.pageSize, filtered.length)}</strong>
                                        {" sur "}
                                        <strong>{filtered.length}</strong>
                                    </span>
                                    <div className="admin-sd-pagination-controls">
                                        <button
                                            className="admin-sd-btn admin-sd-btn-icon"
                                            disabled={currentPage === 1}
                                            onClick={() =>
                                                this.setState(prev => ({ page: Math.max(1, prev.page - 1) }))
                                            }
                                        >
                                            <i className="zmdi zmdi-chevron-left" />
                                        </button>
                                        <span className="admin-sd-page-info">
                                            {currentPage} / {totalPages}
                                        </span>
                                        <button
                                            className="admin-sd-btn admin-sd-btn-icon"
                                            disabled={currentPage === totalPages}
                                            onClick={() =>
                                                this.setState(prev => ({
                                                    page: Math.min(totalPages, prev.page + 1),
                                                }))
                                            }
                                        >
                                            <i className="zmdi zmdi-chevron-right" />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Dialog photos */}
                {dialog.open && dialog.suivi && currentPhoto && (
                    <div className="admin-sd-dialog-overlay" onClick={() => this._closeDialog()}>
                        <div className="admin-sd-dialog" onClick={e => e.stopPropagation()}>
                            <div className="admin-sd-dialog-header">
                                <div>
                                    <h2 className="admin-sd-dialog-title">
                                        <i className="zmdi zmdi-collection-image" />
                                        Photos du suivi
                                    </h2>
                                    <p className="admin-sd-dialog-desc">
                                        {patientName(dialog.suivi)} •{" "}
                                        {mounted
                                            ? new Date(dialog.suivi.createdAt).toLocaleString("fr-FR", {
                                                day: "2-digit",
                                                month: "long",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })
                                            : new Date(dialog.suivi.createdAt)
                                                .toISOString()
                                                .slice(0, 16)
                                                .replace("T", " ")}
                                    </p>
                                </div>
                                <button
                                    className="admin-sd-dialog-close"
                                    onClick={() => this._closeDialog()}
                                    aria-label="Fermer"
                                >
                                    <i className="zmdi zmdi-close" />
                                </button>
                            </div>
                            <div className="admin-sd-dialog-body">
                                <div className="admin-sd-photo-frame">
                                    <img src={currentPhoto.path} alt={currentPhoto.name} />
                                </div>
                                <div className="admin-sd-dialog-controls">
                                    <span className="admin-sd-muted">
                                        Photo {dialog.index + 1} / {dialog.suivi.photoFiles.length}
                                    </span>
                                    <div style={{ display: "flex", gap: 6 }}>
                                        <button
                                            className="admin-sd-btn admin-sd-btn-icon"
                                            disabled={dialog.index === 0}
                                            onClick={() =>
                                                this.setState(prev => ({
                                                    dialog: { ...prev.dialog, index: prev.dialog.index - 1 },
                                                }))
                                            }
                                        >
                                            <i className="zmdi zmdi-chevron-left" />
                                        </button>
                                        <button
                                            className="admin-sd-btn admin-sd-btn-icon"
                                            disabled={dialog.index >= dialog.suivi.photoFiles.length - 1}
                                            onClick={() =>
                                                this.setState(prev => ({
                                                    dialog: { ...prev.dialog, index: prev.dialog.index + 1 },
                                                }))
                                            }
                                        >
                                            <i className="zmdi zmdi-chevron-right" />
                                        </button>
                                        <a
                                            className="admin-sd-btn admin-sd-btn-primary"
                                            href={currentPhoto.path}
                                            target="_blank"
                                            rel="noreferrer"
                                            download
                                        >
                                            <i className="zmdi zmdi-download" />
                                            Télécharger
                                        </a>
                                    </div>
                                </div>
                                {dialog.suivi.photoFiles.length > 1 && (
                                    <div className="admin-sd-thumbs">
                                        {dialog.suivi.photoFiles.map((p, i) => (
                                            <button
                                                key={p._id}
                                                className={`admin-sd-thumb ${i === dialog.index ? "active" : ""}`}
                                                onClick={() =>
                                                    this.setState(prev => ({
                                                        dialog: { ...prev.dialog, index: i },
                                                    }))
                                                }
                                            >
                                                <img src={p.path} alt={p.name} />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default SuiviDermatoTable;
